import { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) setVisible(true);
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({ essential: true, analytics: true, marketing: true, date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({ ...prefs, date: new Date().toISOString() }));
    setVisible(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({ essential: true, analytics: false, marketing: false, date: new Date().toISOString() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      {!showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-gray-950 border-t border-gray-800 shadow-2xl" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Cookie size={20} className="text-amber-400 flex-shrink-0" />
              <p className="text-gray-300 text-sm">
                We use essential cookies for authentication and session management. No third-party tracking.{' '}
                <button onClick={() => setShowPreferences(true)} className="text-primary underline hover:text-primary-hover" data-testid="btn-cookie-preferences">
                  Manage preferences
                </button>
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={handleRejectAll} className="text-gray-400 hover:text-white text-sm font-medium px-4 py-2 border border-gray-700 rounded-lg transition-colors" data-testid="btn-reject-cookies">
                Reject All
              </button>
              <button onClick={handleAcceptAll} className="bg-primary hover:bg-primary-hover text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors" data-testid="btn-accept-cookies">
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[80] p-4" data-testid="cookie-preferences-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="font-manrope font-bold text-lg text-text-primary">Cookie Preferences</h2>
              <button onClick={() => setShowPreferences(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X size={18} className="text-text-muted" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-text-primary">Essential Cookies</p>
                  <p className="text-xs text-text-muted mt-0.5">Required for authentication and core functionality</p>
                </div>
                <input type="checkbox" checked={true} disabled className="accent-primary w-4 h-4" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-text-primary">Analytics Cookies</p>
                  <p className="text-xs text-text-muted mt-0.5">Help us understand how you use the platform</p>
                </div>
                <input type="checkbox" checked={prefs.analytics} onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })} className="accent-primary w-4 h-4" data-testid="check-analytics" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-text-primary">Marketing Cookies</p>
                  <p className="text-xs text-text-muted mt-0.5">Used for personalized communications</p>
                </div>
                <input type="checkbox" checked={prefs.marketing} onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })} className="accent-primary w-4 h-4" data-testid="check-marketing" />
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex gap-3">
              <button onClick={handleRejectAll} className="flex-1 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-medium text-text-primary hover:border-gray-300 transition-colors">
                Reject All
              </button>
              <button onClick={handleSavePreferences} className="flex-1 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg text-sm font-semibold transition-colors" data-testid="btn-save-cookie-prefs">
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function openCookiePreferences() {
  localStorage.removeItem('cookie_consent');
  window.dispatchEvent(new Event('storage'));
}
